import { supabase } from '@/lib/supabase';
import { redirectToCheckout } from '@/lib/stripe';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import Logger from "@/lib/logger";

// Constants
export const PREMIUM_PRICE_ID = import.meta.env.VITE_STRIPE_PRICE_ID;

// Types
export interface PremiumStatus {
  isPremium: boolean;
  premiumUntil: Date | null;
  subscriptionId: string | null;
}

/**
 * Get premium status for a user
 */
export async function getPremiumStatus(userId: string): Promise<PremiumStatus> {
  if (!userId) {
    return {
      isPremium: false,
      premiumUntil: null,
      subscriptionId: null,
    };
  }

  const { data, error } = await supabase
    .from('profiles')
    .select('is_premium, premium_until, stripe_subscription_id')
    .eq('id', userId)
    .single();

  if (error) {
    Logger.error('Error fetching premium status:', error);
    throw error;
  }

  // Check if premium has expired
  const premiumUntil = data.premium_until ? new Date(data.premium_until) : null;
  const isPremium = data.is_premium && 
                  (!premiumUntil || premiumUntil > new Date());

  return {
    isPremium,
    premiumUntil,
    subscriptionId: data.stripe_subscription_id || null,
  };
}

/**
 * Upgrade to premium
 */
export async function upgradeToPremium(userId: string): Promise<void> {
  if (!userId) {
    throw new Error('User ID is required');
  }

  try {
    await redirectToCheckout(PREMIUM_PRICE_ID, userId);
  } catch (error) {
    Logger.error('Error upgrading to premium:', error);
    throw error;
  }
}

/**
 * Manage subscription 
 */
export async function manageSubscription(userId: string | null) {
  if (!userId) {
    toast.error('You must be logged in to manage your subscription');
    return;
  }

  try {
    const { data, error } = await supabase.functions.invoke('customer-portal', {
      body: { userId },
    });

    if (error) throw error;

    if (data?.url) {
      window.location.href = data.url;
    } else {
      throw new Error('No portal URL returned');
    }
  } catch (error) {
    Logger.error('Error opening customer portal:', error);
    toast.error('Failed to open subscription management portal');
  }
}

/**
 * Check if user is premium
 */
export function usePremiumStatus(userId: string | null) {
  const queryClient = useQueryClient();
  
  const { data: status, isLoading: loading } = useQuery({
    queryKey: ['premium-status', userId],
    queryFn: () => getPremiumStatus(userId || ''),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true, // Refresh when window gets focus
  });
  
  // Provide a mutation to update premium status optimistically when needed
  const updatePremiumStatusMutation = useMutation({
    mutationFn: async (newStatus: Partial<PremiumStatus>) => {
      if (!userId) throw new Error('No user ID provided');
      
      // Only update fields that we might need to change
      const updates: Record<string, any> = {};
      
      if (typeof newStatus.isPremium === 'boolean') {
        updates.is_premium = newStatus.isPremium;
      }
      
      if (newStatus.premiumUntil) {
        updates.premium_until = newStatus.premiumUntil.toISOString();
      }
      
      if (typeof newStatus.subscriptionId === 'string') {
        updates.stripe_subscription_id = newStatus.subscriptionId;
      }
      
      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', userId)
        .select('is_premium, premium_until, stripe_subscription_id')
        .single();
        
      if (error) throw error;
      
      return {
        isPremium: data.is_premium,
        premiumUntil: data.premium_until ? new Date(data.premium_until) : null,
        subscriptionId: data.stripe_subscription_id || null,
      };
    },
    onMutate: async (newStatus) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['premium-status', userId] });
      
      // Snapshot current value
      const previousStatus = queryClient.getQueryData<PremiumStatus>(['premium-status', userId]);
      
      // Optimistically update
      if (previousStatus) {
        const optimisticStatus = {
          ...previousStatus,
          ...newStatus,
        };
        
        queryClient.setQueryData(['premium-status', userId], optimisticStatus);
      }
      
      // Return previous value for rollback
      return { previousStatus };
    },
    onError: (error, _, context) => {
      // Revert to previous status on error
      if (context?.previousStatus) {
        queryClient.setQueryData(['premium-status', userId], context.previousStatus);
      }
      Logger.error('Error updating premium status:', error);
      toast.error('Failed to update premium status');
    },
    onSuccess: (newStatus) => {
      // Update with actual server data
      queryClient.setQueryData(['premium-status', userId], newStatus);
    },
  });

  return { 
    status: status || { 
      isPremium: false, 
      premiumUntil: null, 
      subscriptionId: null 
    }, 
    loading,
    updatePremiumStatus: updatePremiumStatusMutation.mutate
  };
} 